import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Col, Row } from 'antd';
import { MailFilled, PlayCircleFilled } from '@ant-design/icons';
import { useMedia } from '@/hooks';
import logo from '@/assets/images/icon.png';
import { userDataContext } from '@/context/AuthContext';
import LoadingOverlay from '@/utils/loading/loadingOverlay';
import '@/assets/style/admin/base.scss';
import '@/assets/style/admin/modern-login.scss';
import {
  rotateTabWithinForm,
  useInputRef,
  validateMobileNumber,
} from '@/utils/validation/FormValidation';
import AdminLogin from './AdminLogin';
import UserLogin from './UserLogin';
import 'react-toastify/dist/ReactToastify.css';
import SuperAdminLogin from './SuperAdminLogin';
import ToastProvider from '@/context/ToastContext';
import { useEffect } from 'react';

const Login = () => {
  const navigate = useNavigate();
  const { handleLogin, loading, successLoading } = userDataContext();
  const { firstInputRef, lastInputRef } = useInputRef();
  const location = useLocation();
  const isMobile = useMedia('mobile');

  const handleFormKeyDown = (e) => {
    rotateTabWithinForm(e, firstInputRef, lastInputRef);
  };

  const loginToggle = (layout) => {
    if (layout.startsWith('/admin')) {
      return (
        <>
          <AdminLogin
            validateMobileNumber={validateMobileNumber}
            firstInputRef={firstInputRef}
            loading={loading}
            handleLogin={handleLogin}
          />
        </>
      );
    } else if (layout.startsWith('/superadmin')) {
      return (
        <ToastProvider>
          <SuperAdminLogin
            validateMobileNumber={validateMobileNumber}
            firstInputRef={firstInputRef}
            loading={loading}
            handleLogin={handleLogin}
          />
        </ToastProvider>
      );
    } else {
      return (
        <UserLogin
          navigate={navigate}
          validateMobileNumber={validateMobileNumber}
          firstInputRef={firstInputRef}
          loading={loading}
          successLoading={successLoading}
          handleLogin={handleLogin}
        />
      );
    }
  };

  useEffect(() => {
    localStorage.removeItem('requirePasswordChange');
    localStorage.removeItem('otp');
  }, []);

  return (
    <div className="modern-login-container" onKeyDown={handleFormKeyDown}>
      <Row
        className="login-row"
        justify="center"
        align="middle"
        style={{ minHeight: '100vh' }}
      >
        {/* Left side - Branding Section */}
        {!isMobile && (
          <Col className="branding-section" xs={0} md={12} lg={10}>
            <div className="branding-content">
              <div className="logo-section-large">
                <img
                  src={logo}
                  alt="Logo"
                  className="animated-logo"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>
              <div className="simple-company-name">
                DipArtha - Powered by Autonomous Technology
              </div>
              <h1 className="brand-title">Welcome Back</h1>
              <p className="brand-subtitle">
                Sign in to access your jewelry management system
              </p>
              <div className="feature-list">
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <div className="feature-text">Secure & Reliable</div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <div className="feature-text">Real-time Updates</div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <div className="feature-text">Easy to Use</div>
                </div>
              </div>
            </div>
          </Col>
        )}

        {/* Right side - Login Form */}
        <Col className="form-section" xs={24} md={12} lg={14}>
          <div className="form-wrapper">
            <Card className="modern-login-card" bordered={false}>
              <LoadingOverlay loading={loading} />

              {/* Mobile Logo - Only on Mobile */}
              {isMobile && (
                <div className="mobile-logo-wrapper">
                  <img
                    src={logo}
                    alt="Logo"
                    className="mobile-logo"
                    draggable={false}
                  />
                  <div className="simple-mobile-company">
                    DipArtha - Powered by Autonomous Technology
                  </div>
                </div>
              )}

              <div className="login-header">
                <h2 className="login-title">Sign In</h2>
                <p className="login-subtitle">
                  Enter your credentials to continue
                </p>
              </div>

              {loginToggle(location.pathname)}

              <div className="login-footer">
                <Button
                  type="link"
                  icon={<MailFilled />}
                  className="footer-link"
                >
                  Contact Support
                </Button>
                <Button
                  type="link"
                  icon={<PlayCircleFilled />}
                  ref={lastInputRef}
                  className="footer-link"
                >
                  Try Demo
                </Button>
              </div>
            </Card>

            <p className="copyright">
              © {new Date().getFullYear()} DipArtha - Powered by Autonomous
              Technology DipArtha. All rights reserved.
            </p>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Login;
